import type { EditorAdapter } from '../adapters/adapter'
import { useEffect, useRef, useState } from 'react'

interface PreviewHtml {
  latest: string
  loading: boolean
}

export function usePreviewStore(adapter: EditorAdapter) {
  const [source, setSource] = useState<string | undefined>(undefined)
  const [html, setHtml] = useState<PreviewHtml>({ latest: '', loading: false })

  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined)
  const ctrlRef = useRef<AbortController>(undefined)

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current)
      ctrlRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (source === undefined)
      return
    let cancelled = false
    setHtml(h => ({ ...h, loading: true }))
    void fetchPreview(source).then((result) => {
      if (cancelled)
        return
      setHtml({ latest: result, loading: false })
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react/exhaustive-deps
  }, [source])

  function refresh(markdown: string) {
    clearTimeout(timerRef.current)
    ctrlRef.current?.abort()
    setSource(markdown)
  }

  function scheduleRefresh(markdown: string) {
    clearTimeout(timerRef.current)
    timerRef.current = setTimeout(setSource, 800, markdown)
  }

  async function fetchPreview(content: string): Promise<string> {
    if (!adapter.previewUrl)
      return ''

    ctrlRef.current = new AbortController()

    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
    try {
      const resp = await fetch(adapter.previewUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': token,
        },
        body: new URLSearchParams({ text: content }),
        signal: ctrlRef.current.signal,
      })
      return resp.text()
    }
    catch {
      return ''
    }
    finally {
      ctrlRef.current = undefined
    }
  }

  return { html, refresh, scheduleRefresh }
}
